import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { NOM_SOCIETE, INITIALES_SOCIETE } from './FormuleAssu';
import {
  ShieldCheck, Scale, Phone, MessageSquare, CheckCircle2, Send, Headphones,
  ClipboardCheck, BadgeCheck, Handshake, FileSearch, Gavel, FileSignature,
  Building2, ChevronDown, ArrowRight, Landmark, FileCheck, Banknote, Receipt, Lock,
  Play, X
} from 'lucide-react';

const NAVY = '#2d3f7f';
const NAVY_DARK = '#1d2b57';
const NAVY_SOFT = '#eef2fb';
const IMG = '/img/formation';

// Petit hook maison : ajoute la classe « visible » quand l'élément entre à l'écran.
function useReveal(active = true) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !active) return undefined;
    if (!('IntersectionObserver' in window)) return undefined;

    // On n'arme le masquage qu'ici : sans JS, ou si cet effet ne tourne pas,
    // le texte reste visible plutôt que de disparaître.
    el.classList.add('fa-armed');

    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('visible'); io.unobserve(e.target); } });
    }, { threshold: 0.08 });
    const nodes = el.querySelectorAll('.reveal');
    nodes.forEach(n => io.observe(n));

    // Filet de sécurité : si quoi que ce soit empêche l'observation, tout redevient visible.
    const secours = setTimeout(() => {
      nodes.forEach(n => n.classList.add('visible'));
    }, 2500);

    return () => { io.disconnect(); clearTimeout(secours); };
  }, [active]);
  return ref;
}

// Maquette téléphone : appel en cours, minuteur qui tourne, ondes sonores,
// et les contrôles du dossier qui se cochent les uns après les autres.
function PhoneMockup() {
  const [sec, setSec] = useState(134);
  useEffect(() => {
    const t = setInterval(() => setSec(v => (v + 1) % 3600), 1000);
    return () => clearInterval(t);
  }, []);
  const mmss = `${String(Math.floor(sec / 60)).padStart(2, '0')}:${String(sec % 60).padStart(2, '0')}`;
  const bars = [0.10, 0.34, 0.02, 0.46, 0.20, 0.58, 0.14, 0.40, 0.06, 0.50, 0.26, 0.62, 0.16, 0.38, 0.08];

  return (
    <div className="fa-phone-holder">
      <div className="fa-phone">
        <div className="fa-badge-float f1"><ShieldCheck size={14} /> Dossier expertisé</div>
        <div className="fa-badge-float f2"><Handshake size={14} /> Accord trouvé</div>
        <div className="fa-notch" />
        <div className="fa-screen">
          <div className="fa-call-top">
            <div className="fa-avatar">{INITIALES_SOCIETE}</div>
            <div style={{ fontWeight: 800, color: NAVY, fontSize: 14.5 }}>{NOM_SOCIETE}</div>
            <div style={{ fontSize: 12, color: '#8a94ad', marginTop: 2 }}>Appel sortant · {mmss}</div>
            <div className="fa-live"><i />ENREGISTREMENT EN COURS</div>
          </div>

          <div className="fa-wave">
            {bars.map((d, i) => (
              <span key={i} style={{ animationDelay: `${d}s` }} />
            ))}
          </div>

          <div style={{ fontSize: 10, fontWeight: 800, color: '#8a94ad', letterSpacing: 1.4, marginBottom: 8 }}>
            DOSSIER À L'ÉCRAN
          </div>
          {[
            ['Contrat signé électroniquement', 0.5],
            ['Enregistrement rattaché au n°', 1.1],
            ['Historique des prélèvements', 1.7]
          ].map(([label, delay]) => (
            <div key={label} className="fa-chk" style={{ animationDelay: `${delay}s` }}>
              <b>✓</b>{label}
            </div>
          ))}

          <div style={{ marginTop: 'auto', display: 'flex', gap: 8, paddingTop: 12 }}>
            <div style={{
              flex: 1, background: '#e6f5ec', color: '#2e9e5b', borderRadius: 11, padding: '9px 0',
              textAlign: 'center', fontSize: 11.5, fontWeight: 800
            }}>Échéancier</div>
            <div style={{
              flex: 1, background: NAVY, color: '#fff', borderRadius: 11, padding: '9px 0',
              textAlign: 'center', fontSize: 11.5, fontWeight: 800
            }}>Régler</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Maquette de la fenêtre CRM : les pièces du dossier validées une à une.
function DossierMockup() {
  return (
    <div className="fa-win">
      <div className="fa-win-bar">
        <span className="fa-dot" style={{ background: '#ff5f57' }} />
        <span className="fa-dot" style={{ background: '#febc2e' }} />
        <span className="fa-dot" style={{ background: '#28c840' }} />
        <span className="fa-win-url">cabinet-des-accords · dossier ………</span>
      </div>
      <div className="fa-win-body">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 11, background: NAVY, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13
          }}>CA</div>
          <div style={{ flex: 1 }}>
            <div className="fa-skeleton" style={{ width: '58%', marginBottom: 6 }} />
            <div className="fa-skeleton" style={{ width: '34%', height: 6 }} />
          </div>
          <div className="fa-ok">EXPERTISÉ</div>
        </div>

        {[
          [FileSignature, 'Contrat signé électroniquement', 'Vérifié', 0.2],
          [Phone, 'Enregistrement rattaché au numéro', 'Vérifié', 0.55],
          [Receipt, 'Historique des prélèvements', 'Complet', 0.9]
        ].map(([Ico, label, tag, delay]) => (
          <div key={label} className="fa-row" style={{ animationDelay: `${delay}s` }}>
            <div className="fa-row-ico"><Ico size={16} style={{ color: NAVY }} /></div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#3c4763' }}>{label}</div>
            <span className="fa-ok">{tag}</span>
          </div>
        ))}

        <div style={{
          marginTop: 13, padding: '11px 13px', borderRadius: 11, background: NAVY_SOFT,
          border: '1px solid #dbe3f5', fontSize: 12, color: NAVY, fontWeight: 700,
          display: 'flex', alignItems: 'center', gap: 8
        }}>
          <CheckCircle2 size={15} /> Dossier validé — prêt à être attribué
        </div>
      </div>
    </div>
  );
}

const VIDEOS = [
  {
    id: 'j51ddZCfyT8',
    vignette: 'video-1',
    titre: "Comment on engage quelqu'un sans qu'il le sache",
    resume: "Le faux conseiller, l'appel qui inspire confiance, la signature obtenue en quelques minutes. Beaucoup de nos dossiers commencent exactement comme ça."
  },
  {
    id: '1HoU17dIEos',
    vignette: 'video-2',
    titre: "À quoi ressemble un plateau qui abuse",
    resume: "De l'autre côté du combiné, une organisation, des scripts et des objectifs. Savoir à quoi ça ressemble aide à comprendre ce que la personne au bout du fil a vécu."
  }
];
const ACCESS_CODE = '2626';
const ACCESS_KEY = 'formationAccesOk';

// Page réservée : elle ne s'ouvre qu'avec le code d'accès.
// Une fois saisi, il est mémorisé sur l'appareil pour ne pas être redemandé.
function CodeGate({ onUnlock }) {
  const [code, setCode] = useState('');
  const [erreur, setErreur] = useState(false);

  const valider = (e) => {
    e.preventDefault();
    if (code.trim() === ACCESS_CODE) {
      try { localStorage.setItem(ACCESS_KEY, 'ok'); } catch (_) { /* navigation privée */ }
      onUnlock();
    } else {
      setErreur(true);
      setCode('');
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 22, position: 'relative', overflow: 'hidden',
      background: `linear-gradient(125deg, ${NAVY_DARK} 0%, ${NAVY} 60%, #3d55b0 100%)`,
      fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
    }}>
      <style>{`
        .gate-blob { position:absolute; border-radius:50%; filter:blur(80px); opacity:.45;
                     animation:gDrift 16s ease-in-out infinite; }
        @keyframes gDrift { 0%,100% { transform:translate(0,0) scale(1);} 50% { transform:translate(30px,-24px) scale(1.1);} }
        @keyframes gShake { 0%,100% { transform:translateX(0);} 25% { transform:translateX(-8px);} 75% { transform:translateX(8px);} }
        .gate-card { position:relative; z-index:1; background:rgba(255,255,255,.97); border-radius:22px;
                     padding:40px 34px; width:100%; max-width:400px; text-align:center;
                     box-shadow:0 34px 80px rgba(0,0,0,.42); }
        .gate-card.err { animation:gShake .38s ease; }
        .gate-input { width:100%; padding:15px; border:1.5px solid #d5dcec; border-radius:13px; box-sizing:border-box;
                      font-size:27px; text-align:center; letter-spacing:11px; font-weight:800; color:${NAVY};
                      font-family:inherit; transition:border-color .2s, box-shadow .2s; }
        .gate-input:focus { outline:none; border-color:${NAVY}; box-shadow:0 0 0 4px ${NAVY}1c; }
      `}</style>
      <span className="gate-blob" style={{ width: 380, height: 380, background: '#5a76e0', top: -120, right: -80 }} />
      <span className="gate-blob" style={{ width: 300, height: 300, background: '#7b5fd1', bottom: -100, left: -60, animationDelay: '-7s' }} />

      <form className={`gate-card${erreur ? ' err' : ''}`} onSubmit={valider}>
        <div style={{
          width: 60, height: 60, borderRadius: 18, margin: '0 auto 18px',
          background: `linear-gradient(140deg, ${NAVY}, #5f7ae0)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <Lock size={26} style={{ color: '#fff' }} />
        </div>
        <h1 style={{ fontSize: 22, color: NAVY, margin: '0 0 8px', fontWeight: 800 }}>Accès réservé</h1>
        <p style={{ color: '#6b7590', fontSize: 14.5, margin: '0 0 22px' }}>
          Cette page de formation est protégée. Saisissez le code qui vous a été communiqué.
        </p>

        <input
          className="gate-input"
          type="tel"
          inputMode="numeric"
          autoFocus
          maxLength={8}
          placeholder="••••"
          value={code}
          onChange={(e) => { setCode(e.target.value); setErreur(false); }}
        />

        {erreur && (
          <div style={{ color: '#c62828', fontSize: 13.5, fontWeight: 600, marginTop: 12 }}>
            Code incorrect. Réessayez.
          </div>
        )}

        <button type="submit" style={{
          width: '100%', marginTop: 18, padding: '15px 0', border: 'none', borderRadius: 13,
          background: `linear-gradient(135deg, ${NAVY}, #4a63c8)`, color: '#fff',
          fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit'
        }}>
          Accéder à la formation
        </button>
      </form>
    </div>
  );
}

function FormationAssurance() {
  const [formData, setFormData] = useState({ nom: '', telephone_whatsapp: '', email_pro: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [openModule, setOpenModule] = useState(0);
  const [progress, setProgress] = useState(0);
  const [videoOuverte, setVideoOuverte] = useState(null);

  // Lecteur vidéo : fermeture à la touche Échap et blocage du défilement de fond
  useEffect(() => {
    if (!videoOuverte) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setVideoOuverte(null); };
    const scrollAvant = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = scrollAvant;
    };
  }, [videoOuverte]);

  // Accès : code déjà validé sur cet appareil, ou passé dans l'URL (?code=…) pour un lien direct.
  const [unlocked, setUnlocked] = useState(() => {
    try {
      if (new URLSearchParams(window.location.search).get('code') === ACCESS_CODE) {
        localStorage.setItem(ACCESS_KEY, 'ok');
        return true;
      }
      return localStorage.getItem(ACCESS_KEY) === 'ok';
    } catch (_) {
      return false;
    }
  });

  // Barre de progression de lecture en haut de page
  useEffect(() => {
    let frame = null;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        const h = document.documentElement.scrollHeight - window.innerHeight;
        setProgress(h > 0 ? Math.min(100, (window.scrollY / h) * 100) : 0);
        frame = null;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  const rootRef = useReveal(unlocked);
  const API_URL = process.env.REACT_APP_API_URL || '';

  const currentDate = new Date().toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });

  useEffect(() => {
    if (!unlocked) return;
    axios.post(`${API_URL}/api/link-clicks/formation-assurance`, {
      language: navigator.language,
      platform: navigator.userAgentData?.platform || navigator.platform,
      screen_width: window.screen.width,
      screen_height: window.screen.height
    }).catch(() => {});
  }, [API_URL, unlocked]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/candidats-assu/public`, { ...formData, source: 'formation-assurance' });
      setSuccess(true);
      const el = document.getElementById('candidature');
      if (el) window.scrollTo({ top: el.offsetTop - 60, behavior: 'smooth' });
    } catch (err) {
      setError(err.response?.data?.error || 'Une erreur est survenue. Merci de réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const goTo = (id) => {
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.offsetTop - 56, behavior: 'smooth' });
  };

  // Bandeau défilant du hero : les arguments qui donnent envie de postuler.
  const bandeau = [
    'Formation assurée',
    'Aucun diplôme exigé',
    'Plateau à taille humaine',
    'On vous forme, vous décrochez',
    'Double écoute dès le premier appel',
    'Un non n\'est jamais une fin',
    'Ici on compte les dossiers réglés',
    'Personne ne rame seul',
    'Évolution selon vos résultats',
    'Un métier qui s\'apprend vite',
    'Chaque appel est une nouvelle chance',
    'Horaires fixes, rythme tenu'
  ];

  const troisMetiers = [
    { icon: Building2, t: 'Recouvrement', d: "Les créanciers nous confient leurs dossiers. À nous d'aller chercher le règlement, au téléphone, un dossier après l'autre." },
    { icon: Handshake, t: 'Conciliation', d: "On ne cherche pas le conflit, on cherche l'accord. Un dossier qu'on referme, c'est un client qui sort de la spirale." },
    { icon: ShieldCheck, t: 'Défense du client', d: "Et quand le client a raison, on le rembourse et on va chercher des dommages et intérêts chez le créancier. C'est ce qui fait qu'on n'a jamais honte de décrocher." }
  ];

  const issues = [
    {
      n: '01', img: 'justice', color: '#8a5a1f', icon: Gavel,
      titre: 'Il refuse — le dossier avance sans lui',
      court: "On a tout tenté, il ne veut rien entendre. Le dossier repart au créancier, qui peut porter l'affaire devant le juge.",
      long: "C'est l'issue la plus coûteuse pour lui, et c'est exactement ce qu'on lui aura expliqué calmement avant d'en arriver là."
    },
    {
      n: '02', img: 'enquete', color: NAVY, icon: FileSearch,
      titre: 'Il conteste — on retourne le dossier',
      court: "Il jure n'avoir jamais rien signé ? Une dénégation ne referme pas un dossier documenté. On reprend les faits avec lui : date, numéro, SMS reçu, prélèvements.",
      long: "Et si sa contestation tient, on ne s'arrête pas au constat : le client est remboursé, et le Cabinet se retourne contre le créancier pour réclamer des dommages et intérêts. C'est le moment où on cesse d'être l'intermédiaire pour devenir son meilleur allié."
    },
    {
      n: '03', img: 'accord', color: '#2e9e5b', icon: Handshake,
      titre: 'Il règle — dossier clos',
      court: "En une fois ou en plusieurs. Le dossier se referme, le client sort de la spirale, et vous marquez le point.",
      long: "C'est l'issue qu'on va chercher sur la grande majorité des appels. Confirmation écrite envoyée dans la foulée."
    }
  ];

  const pieces = [
    { t: 'Le contrat signé électroniquement', d: "Signé depuis le numéro sur lequel nous appelons — c'est ce qui rattache la souscription à sa ligne." },
    { t: "L'enregistrement de la souscription", d: "L'appel au cours duquel le contrat a été conclu, fourni par la compagnie." },
    { t: "L'historique des prélèvements", d: "Les échéances passées, les impayées, et le montant exact réclamé au jour de l'appel." }
  ];

  const modules = [
    {
      icon: Phone, titre: 'Ouvrir et prendre la main',
      resume: "Se présenter, poser le cadre, annoncer l'objet en dix secondes. Les trente premières secondes décident du reste de l'appel.",
      extrait: `« Bonjour Monsieur / Madame ………, ……… du ${NOM_SOCIETE}. Cet appel est enregistré. Je vous informe que vous avez un dossier auprès de nos services, arrivé à sa date d'échéance le ………. Il faut régulariser la situation aujourd'hui, sinon nous serons dans l'obligation de remettre votre dossier au créancier, qui engagera une mise en demeure. »`,
      note: "L'échéance et la conséquence dès l'ouverture : c'est ce qui fait obtenir une décision au lieu d'un « rappelez-moi »."
    },
    {
      icon: FileSignature, titre: "Annoncer le montant sans trembler",
      resume: "Le chiffre exact, la période, la date. On l'annonce franchement, puis on se tait et on écoute.",
      extrait: "« Le dossier porte sur ……… € correspondant aux échéances de ……… à ………. Il arrive à son terme de traitement amiable le ………. Avant d'aller plus loin : est-ce que ce contrat vous parle ? »",
      note: "Un montant annoncé d'une voix ferme passe. Un montant murmuré ou arrondi déclenche la méfiance immédiate."
    },
    {
      icon: MessageSquare, titre: 'Encaisser les objections et rebondir',
      resume: "« Pas intéressé », « je n'ai pas les moyens », « j'ai jamais rien signé ». Chacune a sa réponse. Un premier non n'est pas une réponse, c'est une porte à ouvrir.",
      extrait: "« Je vous entends, et vous n'êtes pas le premier à ne pas s'en souvenir — un contrat souscrit par téléphone, ça remonte vite. Mais un dossier existe bel et bien, et il a été expertisé avant que je vous appelle : il nous a été remis un contrat signé électroniquement depuis votre numéro, et un enregistrement du service qualité où l'engagement est validé. Ce sont des éléments datés, et le dossier suit son cours. Alors prenons le temps de regarder ça ensemble, je suis là pour vous aider à y voir plus clair : la souscription date du ………, à ……… heures — est-ce que ça vous revient ? Et si vous maintenez que ce n'est pas vous, je le note et j'ouvre l'instruction : dans ce cas c'est le créancier qui rendra des comptes, pas vous. »",
      note: "Le premier argument, c'est le dossier — pas la dette. Il existe, il a été expertisé, il est daté. Une dénégation de confort ne tient pas trente secondes face à une date et une heure."
    },
    {
      icon: ClipboardCheck, titre: 'Conclure',
      resume: "Règlement, échéancier, ou instruction du dossier. On ne raccroche pas sans avoir posé une décision et une date.",
      extrait: "« Deux possibilités, et c'est vous qui choisissez. Soit on referme aujourd'hui, sur la totalité ou sur un échéancier que vous tenez. Soit vous contestez, et j'ouvre l'instruction. Qu'est-ce qui correspond à votre situation ? »",
      note: "Puis se taire. Le silence travaille pour vous — c'est la technique la plus rentable du métier."
    }
  ];

  // Ce qui attend le client s'il ne règle pas : la chaîne complète, telle qu'elle se déroule.
  const procedure = [
    {
      icon: FileSignature, t: 'Mise en demeure',
      d: "Le créancier reprend la main et adresse une mise en demeure. C'est le dernier courrier avant la procédure — et la dernière fenêtre pour régler sans frais de justice."
    },
    {
      icon: Landmark, t: 'Le juge est saisi',
      d: "Deux voies. L'injonction de payer : une requête sur pièces, rapide, sans que le client soit entendu — le juge rend une ordonnance. Ou l'assignation, avec audience et débat contradictoire."
    },
    {
      icon: FileCheck, t: 'Le titre exécutoire',
      d: "L'ordonnance est signifiée au client par un commissaire de justice — le nouveau nom des huissiers. Il a un mois pour faire opposition. S'il ne bouge pas, le titre devient définitif : c'est LA bascule du dossier."
    },
    {
      icon: Banknote, t: "L'exécution forcée",
      d: "À partir de là, le commissaire de justice peut agir. Saisie-attribution sur le compte bancaire — l'équivalent, chez un créancier privé, de l'avis à tiers détenteur du fisc. Saisie des rémunérations directement chez l'employeur, selon un barème légal qui laisse une part insaisissable. Saisie-vente des biens en dernier ressort."
    },
    {
      icon: Receipt, t: "L'addition",
      d: "Une fois le titre obtenu, les frais d'exécution passent à la charge du client, et les intérêts légaux sont majorés deux mois après. La dette réglée à ce stade coûte nettement plus cher que celle réglée au téléphone."
    }
  ];

  const nav = [
    ['metier', 'Le métier'],
    ['verite', 'Le rappel'],
    ['procedure', 'La procédure'],
    ['issues', 'Le job'],
    ['formation', 'La formation'],
    ['cadre', 'Franchement'],
    ['candidature', 'Candidater']
  ];

  if (!unlocked) {
    return <CodeGate onUnlock={() => setUnlocked(true)} />;
  }

  return (
    <div ref={rootRef} translate="no" className="notranslate" style={{ background: '#fff', color: '#333', fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif", lineHeight: 1.6 }}>
      <div className="fa-progress" style={{ width: `${progress}%` }} />

      {/* Lecteur vidéo en surimpression — on reste sur le site */}
      {videoOuverte && (
        <div className="fa-modal" onClick={() => setVideoOuverte(null)}>
          <div className="fa-modal-box" onClick={(e) => e.stopPropagation()}>
            <button className="fa-modal-close" onClick={() => setVideoOuverte(null)}>
              <X size={16} /> Fermer
            </button>
            <div className="fa-modal-video">
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${videoOuverte}?autoplay=1&rel=0`}
                title={`Vidéo — ${NOM_SOCIETE}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
      <style>{`
        .fa-wrap { max-width: 1080px; margin: 0 auto; padding: 0 22px; }
        .fa-armed .reveal { opacity:0; transform:translateY(26px);
                            transition:opacity .7s cubic-bezier(.2,.7,.3,1), transform .7s cubic-bezier(.2,.7,.3,1); }
        .fa-armed .reveal.visible { opacity:1; transform:none; }

        /* ---------- Barre de progression de lecture ---------- */
        .fa-progress { position:fixed; top:0; left:0; height:3px; z-index:99;
                       background:linear-gradient(90deg, #6f86d6, #4a63c8, #8ea2f0); transition:width .1s linear; }

        /* ---------- HERO ---------- */
        .fa-hero { position:relative; overflow:hidden; padding:80px 0 84px;
                   background:${NAVY_DARK} url('${IMG}/hero.jpg') center/cover; }
        .fa-hero::before { content:''; position:absolute; inset:0; z-index:0;
                           background:linear-gradient(120deg, ${NAVY_DARK}fa 0%, ${NAVY_DARK}e8 42%, ${NAVY}b3 100%); }
        .fa-blob { position:absolute; border-radius:50%; filter:blur(70px); opacity:.5; z-index:0;
                   animation:faDrift 17s ease-in-out infinite; }
        .fa-blob.b1 { width:420px; height:420px; background:#4f6fd8; top:-130px; right:-90px; }
        .fa-blob.b2 { width:330px; height:330px; background:#7b5fd1; bottom:-120px; left:-70px; animation-delay:-6s; }
        .fa-blob.b3 { width:250px; height:250px; background:#3fb4c9; top:42%; right:34%; animation-delay:-11s; opacity:.28; }
        @keyframes faDrift {
          0%,100% { transform:translate(0,0) scale(1); }
          33%     { transform:translate(34px,-26px) scale(1.09); }
          66%     { transform:translate(-24px,20px) scale(.94); }
        }
        .fa-hero-in { position:relative; z-index:1; display:grid; grid-template-columns:1.05fr .95fr;
                      gap:44px; align-items:center; }
        .fa-grad { background:linear-gradient(100deg,#fff 20%,#c8d6ff 55%,#fff 85%); -webkit-background-clip:text;
                   background-clip:text; color:transparent; background-size:220% auto; animation:faShine 7s linear infinite; }
        @keyframes faShine { to { background-position:220% center; } }
        .fa-marquee-band { background:linear-gradient(90deg, ${NAVY_DARK}, ${NAVY} 50%, ${NAVY_DARK});
                           border-bottom:1px solid rgba(255,255,255,.1); }
        .fa-marquee { position:relative; overflow:hidden; padding:1px 0;
                      -webkit-mask-image:linear-gradient(90deg, transparent, #000 6%, #000 88%, transparent);
                      mask-image:linear-gradient(90deg, transparent, #000 6%, #000 88%, transparent); }
        .fa-marquee-track { display:flex; width:max-content; animation:faMarquee 62s linear infinite; }
        .fa-marquee:hover .fa-marquee-track { animation-play-state:paused; }
        @keyframes faMarquee { from { transform:translateX(0); } to { transform:translateX(-50%); } }
        .fa-marquee-item { display:inline-flex; align-items:center; gap:22px; white-space:nowrap;
                           padding:26px 0; margin-right:54px; color:rgba(255,255,255,.94);
                           font-size:30px; font-weight:700; letter-spacing:-.2px; }
        .fa-marquee-dot { width:11px; height:11px; border-radius:50%; flex:0 0 auto;
                          background:linear-gradient(140deg,#8ea2f0,#fff); }

        .fa-pill { display:inline-flex; align-items:center; gap:7px; background:rgba(255,255,255,.13);
                   border:1px solid rgba(255,255,255,.28); color:#fff; border-radius:999px;
                   padding:7px 15px; font-size:13.5px; font-weight:600; backdrop-filter:blur(6px);
                   transition:transform .2s, background .2s; }
        .fa-pill:hover { transform:translateY(-2px); background:rgba(255,255,255,.22); }

        /* ---------- Maquette téléphone ---------- */
        .fa-phone-holder { display:flex; justify-content:center; position:relative; animation:faFloat 6s ease-in-out infinite; }
        @keyframes faFloat { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-14px); } }
        .fa-phone { width:266px; border-radius:38px; padding:11px; position:relative;
                    background:linear-gradient(160deg,#2a3358,#161d38);
                    box-shadow:0 40px 80px rgba(0,0,0,.5), 0 0 0 1px rgba(255,255,255,.09) inset; }
        .fa-notch { position:absolute; top:11px; left:50%; transform:translateX(-50%); width:92px; height:19px;
                    background:#161d38; border-radius:0 0 13px 13px; z-index:3; }
        .fa-screen { background:linear-gradient(175deg,#f7f9ff,#e9eeff); border-radius:29px; padding:34px 16px 18px;
                     min-height:430px; display:flex; flex-direction:column; }
        .fa-call-top { text-align:center; margin-bottom:14px; }
        .fa-avatar { width:56px; height:56px; border-radius:50%; margin:0 auto 9px; position:relative;
                     background:linear-gradient(140deg,${NAVY},#5f7ae0); color:#fff; font-weight:800; font-size:19px;
                     display:flex; align-items:center; justify-content:center; }
        .fa-avatar::after { content:''; position:absolute; inset:-7px; border-radius:50%;
                            border:2px solid ${NAVY}; opacity:0; animation:faRing 2.4s ease-out infinite; }
        @keyframes faRing { 0% { transform:scale(.86); opacity:.75; } 100% { transform:scale(1.35); opacity:0; } }
        .fa-live { display:inline-flex; align-items:center; gap:6px; font-size:10.5px; font-weight:800;
                   color:#2e9e5b; background:#e6f5ec; border-radius:999px; padding:3px 9px; margin-top:6px; }
        .fa-live i { width:6px; height:6px; border-radius:50%; background:#2e9e5b; animation:faBlink 1.3s ease-in-out infinite; }
        @keyframes faBlink { 50% { opacity:.25; } }
        .fa-wave { display:flex; align-items:center; justify-content:center; gap:3px; height:38px; margin:6px 0 14px; }
        .fa-wave span { width:3px; border-radius:2px; background:linear-gradient(180deg,#7b91e8,${NAVY});
                        animation:faWave 1.05s ease-in-out infinite; }
        @keyframes faWave { 0%,100% { height:7px; opacity:.55; } 50% { height:30px; opacity:1; } }
        .fa-chk { display:flex; align-items:center; gap:9px; background:#fff; border:1px solid #e2e8f7;
                  border-radius:11px; padding:9px 11px; margin-bottom:7px; font-size:11.5px; font-weight:600;
                  color:#3c4763; opacity:0; transform:translateY(9px); animation:faPop .5s ease forwards; }
        .fa-chk b { display:flex; align-items:center; justify-content:center; width:19px; height:19px; flex:0 0 auto;
                    border-radius:50%; background:#e6f5ec; color:#2e9e5b; font-size:11px; }
        @keyframes faPop { to { opacity:1; transform:none; } }
        .fa-badge-float { position:absolute; background:rgba(255,255,255,.96); border-radius:13px; padding:10px 13px;
                          box-shadow:0 16px 36px rgba(0,0,0,.24); font-size:11.5px; font-weight:700; color:${NAVY};
                          display:flex; align-items:center; gap:7px; z-index:5; white-space:nowrap;
                          animation:faFloat 5s ease-in-out infinite; }
        .fa-badge-float.f1 { top:52px; left:-74px; animation-delay:-1.6s; }
        .fa-badge-float.f2 { bottom:24px; right:-88px; animation-delay:-3.4s; }
        @media (max-width:1080px) {
          .fa-badge-float.f1 { left:-30px; }
          .fa-badge-float.f2 { right:-20px; bottom:14px; }
        }

        /* ---------- Navigation ---------- */
        .fa-nav { position:sticky; top:0; z-index:50; background:rgba(255,255,255,.9); backdrop-filter:blur(14px);
                  border-bottom:1px solid #e6eaf3; }
        .fa-nav-in { max-width:1080px; margin:0 auto; padding:0 14px; display:flex; gap:2px; overflow-x:auto; }
        .fa-nav button { background:none; border:none; padding:15px 14px; font-size:14px; color:#5a6a92;
                         cursor:pointer; font-weight:600; white-space:nowrap; position:relative;
                         font-family:inherit; transition:color .2s; }
        .fa-nav button::after { content:''; position:absolute; left:14px; right:14px; bottom:0; height:2px;
                                background:${NAVY}; transform:scaleX(0); transform-origin:left; transition:transform .28s; }
        .fa-nav button:hover { color:${NAVY}; }
        .fa-nav button:hover::after { transform:scaleX(1); }

        /* ---------- Titres et sections ---------- */
        .fa-h2 { font-size:31px; color:${NAVY}; margin:0 0 12px; font-weight:800; letter-spacing:-.7px; line-height:1.15; }
        .fa-kicker { display:inline-block; font-size:11.5px; letter-spacing:2.5px; text-transform:uppercase;
                     color:${NAVY}; font-weight:800; margin-bottom:9px; background:${NAVY_SOFT};
                     border-radius:999px; padding:5px 13px; }
        .fa-kicker.on-dark { background:rgba(255,255,255,.16); color:#fff;
                             border:1px solid rgba(255,255,255,.24); }
        .fa-sec { padding:76px 0; }
        .fa-lead { color:#5a6a92; font-size:16.5px; margin:0 0 26px; max-width:730px; }

        .fa-card { border:1px solid #e6eaf3; border-radius:18px; padding:24px; background:#fff; height:100%;
                   box-sizing:border-box; position:relative; overflow:hidden;
                   transition:transform .3s cubic-bezier(.2,.7,.3,1), box-shadow .3s, border-color .3s; }
        .fa-card::before { content:''; position:absolute; inset:0 0 auto 0; height:3px;
                           background:linear-gradient(90deg,${NAVY},#7b91e8); transform:scaleX(0);
                           transform-origin:left; transition:transform .35s; }
        .fa-card:hover { transform:translateY(-7px); box-shadow:0 22px 46px rgba(45,63,127,.16); border-color:#d3ddf5; }
        .fa-card:hover::before { transform:scaleX(1); }

        .fa-issue { border:1px solid #e6eaf3; border-radius:20px; overflow:hidden; background:#fff;
                    transition:transform .3s cubic-bezier(.2,.7,.3,1), box-shadow .3s; }
        .fa-issue:hover { transform:translateY(-8px); box-shadow:0 26px 54px rgba(45,63,127,.2); }
        .fa-issue-img { height:168px; background-size:cover; background-position:center; position:relative;
                        transition:transform .6s cubic-bezier(.2,.7,.3,1); }
        .fa-issue:hover .fa-issue-img { transform:scale(1.07); }
        .fa-issue-wrap { overflow:hidden; }
        .fa-issue-img::after { content:''; position:absolute; inset:0;
                               background:linear-gradient(180deg, transparent 30%, rgba(10,16,40,.62) 100%); }
        .fa-issue-n { position:absolute; left:15px; top:15px; z-index:2; color:#fff; font-weight:800;
                      font-size:13px; border-radius:9px; padding:5px 11px; box-shadow:0 6px 16px rgba(0,0,0,.28); }

        /* ---------- Frise de la procédure ---------- */
        .fa-step { display:flex; gap:20px; position:relative; padding-bottom:30px; }
        .fa-step:last-child { padding-bottom:0; }
        .fa-step::before { content:''; position:absolute; left:25px; top:56px; bottom:0; width:2px;
                           background:linear-gradient(180deg,${NAVY}59,#eef2fb);
                           transform-origin:top; transition:transform .8s ease .2s; }
        .fa-armed .fa-step::before { transform:scaleY(0); }
        .fa-armed .fa-step.visible::before { transform:scaleY(1); }
        .fa-step:last-child::before { display:none; }
        .fa-step-n { flex:0 0 auto; width:52px; height:52px; border-radius:17px; background:#fff;
                     border:2px solid #dbe3f5; display:flex; align-items:center; justify-content:center;
                     position:relative; z-index:1; transition:border-color .25s, transform .25s, box-shadow .25s; }
        .fa-step:hover .fa-step-n { border-color:${NAVY}; transform:scale(1.09) rotate(-4deg);
                                    box-shadow:0 12px 26px rgba(45,63,127,.2); }

        /* ---------- Maquette fenêtre CRM ---------- */
        .fa-win { border-radius:16px; overflow:hidden; background:#fff; border:1px solid #dfe5f4;
                  box-shadow:0 30px 64px rgba(45,63,127,.22); transform:perspective(1400px) rotateY(-6deg) rotateX(2deg);
                  transition:transform .5s cubic-bezier(.2,.7,.3,1); }
        .fa-win:hover { transform:perspective(1400px) rotateY(0deg) rotateX(0deg); }
        .fa-win-bar { display:flex; align-items:center; gap:6px; padding:11px 14px;
                      background:linear-gradient(180deg,#f6f8fd,#eef2fa); border-bottom:1px solid #e4eaf6; }
        .fa-dot { width:10px; height:10px; border-radius:50%; }
        .fa-win-url { flex:1; text-align:center; font-size:11px; color:#8a94ad; background:#fff;
                      border:1px solid #e4eaf6; border-radius:7px; padding:3px 10px; margin-left:8px; }
        .fa-win-body { padding:18px; }
        .fa-row { display:flex; align-items:center; gap:11px; padding:12px 13px; border-radius:11px;
                  background:#fbfcfe; border:1px solid #eef1f8; margin-bottom:9px;
                  opacity:0; transform:translateX(-12px); animation:faSlide .55s ease forwards; }
        @keyframes faSlide { to { opacity:1; transform:none; } }
        .fa-row-ico { width:32px; height:32px; border-radius:9px; flex:0 0 auto; display:flex;
                      align-items:center; justify-content:center; background:${NAVY_SOFT}; }
        .fa-ok { margin-left:auto; font-size:10.5px; font-weight:800; color:#2e9e5b; background:#e6f5ec;
                 border-radius:999px; padding:3px 9px; white-space:nowrap; }
        .fa-skeleton { height:8px; border-radius:5px; background:linear-gradient(90deg,#eef1f8,#e2e8f5,#eef1f8);
                       background-size:220% 100%; animation:faShimmer 1.7s linear infinite; }
        @keyframes faShimmer { to { background-position:-220% 0; } }

        /* ---------- Accordéon ---------- */
        .fa-mod { border:1px solid #e6eaf3; border-radius:15px; margin-bottom:11px; overflow:hidden; background:#fff;
                  transition:box-shadow .25s, border-color .25s; }
        .fa-mod:hover { border-color:#d3ddf5; box-shadow:0 10px 26px rgba(45,63,127,.09); }
        .fa-mod-head { width:100%; display:flex; align-items:center; gap:13px; padding:17px 19px; background:none;
                       border:none; cursor:pointer; text-align:left; font-family:inherit; font-size:16px;
                       font-weight:700; color:${NAVY}; transition:background .2s; }
        .fa-mod-head:hover { background:${NAVY_SOFT}; }
        .fa-mod-body { padding:0 19px 19px; animation:faIn .35s cubic-bezier(.2,.7,.3,1); }
        @keyframes faIn { from { opacity:0; transform:translateY(-8px);} to { opacity:1; transform:none;} }
        .fa-script { background:linear-gradient(135deg,#f7f9fd,#eef2fb); border-left:4px solid ${NAVY};
                     border-radius:0 11px 11px 0; padding:15px 19px; margin:13px 0; font-size:14.5px; }
        .fa-note { color:#5a6a92; font-style:italic; font-size:13px; margin:8px 0 0; }

        .fa-split { display:grid; grid-template-columns:1fr 1fr; gap:44px; align-items:center; }
        .fa-photo { border-radius:20px; width:100%; display:block; box-shadow:0 24px 54px rgba(45,63,127,.22);
                    transition:transform .5s cubic-bezier(.2,.7,.3,1); }
        .fa-photo:hover { transform:scale(1.02); }

        .fa-input { width:100%; padding:14px 16px; border:1.5px solid #d5dcec; border-radius:12px; font-size:15px;
                    box-sizing:border-box; font-family:inherit; background:#fff; transition:border-color .2s, box-shadow .2s; }
        .fa-input:focus { outline:none; border-color:${NAVY}; box-shadow:0 0 0 4px ${NAVY}1c; }
        .fa-cta { background:linear-gradient(135deg,${NAVY},#4a63c8); color:#fff; border:none; border-radius:12px;
                  padding:15px 24px; font-size:15.5px; font-weight:700; cursor:pointer; font-family:inherit;
                  display:inline-flex; align-items:center; gap:10px; position:relative; overflow:hidden;
                  transition:transform .22s, box-shadow .22s; }
        .fa-cta::after { content:''; position:absolute; top:0; left:-60%; width:45%; height:100%;
                         background:linear-gradient(90deg,transparent,rgba(255,255,255,.42),transparent);
                         transform:skewX(-20deg); animation:faSheen 3.4s ease-in-out infinite; }
        @keyframes faSheen { 0%,62% { left:-60%; } 88%,100% { left:130%; } }
        .fa-cta:hover { transform:translateY(-3px); box-shadow:0 16px 34px rgba(45,63,127,.36); }
        .fa-cta:disabled { opacity:.6; cursor:not-allowed; transform:none; }

        .fa-cta-ghost { background:rgba(255,255,255,.12); border:1px solid rgba(255,255,255,.34);
                        backdrop-filter:blur(6px); }
        .fa-cta-ghost::after { display:none; }
        .fa-cta-ghost:hover { background:rgba(255,255,255,.22); box-shadow:0 14px 30px rgba(0,0,0,.28); }
        .fa-play { width:26px; height:26px; border-radius:50%; background:#fff; color:${NAVY};
                   display:flex; align-items:center; justify-content:center; flex:0 0 auto; }
        .fa-play svg { margin-left:2px; }

        .fa-vid { display:block; width:100%; text-align:left; padding:0; border:none; cursor:pointer;
                  background:rgba(255,255,255,.07); border:1px solid rgba(255,255,255,.16); border-radius:18px;
                  overflow:hidden; font-family:inherit; color:inherit;
                  transition:transform .3s cubic-bezier(.2,.7,.3,1), box-shadow .3s, border-color .3s; }
        .fa-vid:hover { transform:translateY(-7px); border-color:rgba(255,255,255,.34);
                        box-shadow:0 26px 54px rgba(0,0,0,.42); }
        .fa-vid-img { position:relative; padding-top:56.25%; background-size:cover; background-position:center; }
        .fa-vid-img::after { content:''; position:absolute; inset:0;
                             background:linear-gradient(180deg, rgba(10,16,40,.1), rgba(10,16,40,.55)); }
        .fa-vid-play { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; z-index:2; }
        .fa-vid-play span { width:66px; height:66px; border-radius:50%; background:rgba(255,255,255,.94);
                            display:flex; align-items:center; justify-content:center; color:${NAVY};
                            box-shadow:0 12px 34px rgba(0,0,0,.4); transition:transform .3s; }
        .fa-vid:hover .fa-vid-play span { transform:scale(1.12); }
        .fa-vid-play span svg { margin-left:4px; }
        .fa-vid-body { padding:18px 20px 22px; }

        .fa-modal { position:fixed; inset:0; z-index:200; display:flex; align-items:center; justify-content:center;
                    padding:24px; background:rgba(8,12,28,.88); backdrop-filter:blur(8px); animation:faFade .25s ease; }
        @keyframes faFade { from { opacity:0; } to { opacity:1; } }
        .fa-modal-box { width:100%; max-width:1000px; animation:faZoom .32s cubic-bezier(.2,.7,.3,1); }
        @keyframes faZoom { from { opacity:0; transform:scale(.94) translateY(14px); } to { opacity:1; transform:none; } }
        .fa-modal-video { position:relative; width:100%; padding-top:56.25%; border-radius:16px; overflow:hidden;
                          background:#000; box-shadow:0 40px 90px rgba(0,0,0,.6); }
        .fa-modal-video iframe { position:absolute; inset:0; width:100%; height:100%; border:0; }
        .fa-modal-close { display:flex; align-items:center; gap:7px; margin:0 0 12px auto; background:rgba(255,255,255,.14);
                          border:1px solid rgba(255,255,255,.28); color:#fff; border-radius:10px; padding:9px 15px;
                          font-size:14px; font-weight:600; cursor:pointer; font-family:inherit; transition:background .2s; }
        .fa-modal-close:hover { background:rgba(255,255,255,.26); }

        /* ---------- Fonds de section ---------- */
        .fa-bg { position:relative; isolation:isolate; }
        .fa-bg::before { content:''; position:absolute; inset:0; z-index:-1; background-image:var(--veil), var(--fa-bg);
                         background-size:cover; background-position:center; background-attachment:fixed; }
        .fa-bg.veil-clair { --veil:linear-gradient(rgba(250,251,254,.93), rgba(250,251,254,.95)); }
        .fa-bg.veil-blanc { --veil:linear-gradient(rgba(255,255,255,.94), rgba(255,255,255,.96)); }
        .fa-bg.veil-fort  { --veil:linear-gradient(rgba(250,251,254,.965), rgba(250,251,254,.975)); }

        /* ---------- Bord éclaté entre deux sections ---------- */
        .fa-cut { clip-path:polygon(0 30px, 11% 2px, 23% 26px, 37% 4px, 50% 28px, 63% 6px, 77% 24px, 89% 2px, 100% 26px,
                                    100% 100%, 0 100%); padding-top:104px; margin-top:-30px; }

        /* ---------- Arrivées latérales ---------- */
        .fa-armed .reveal.reveal-l { transform:translateX(-90px) rotate(-1.2deg); }
        .fa-armed .reveal.reveal-r { transform:translateX(90px) rotate(1.2deg); }
        .fa-armed .reveal.reveal-l.visible,
        .fa-armed .reveal.reveal-r.visible { transform:none; }

        /* ---------- Éclatement : chaque carte converge depuis un angle ---------- */
        .fa-armed .burst > * { opacity:0; filter:blur(7px); will-change:transform, opacity;
                               transition:opacity .75s cubic-bezier(.2,.7,.3,1),
                                          transform .85s cubic-bezier(.16,.84,.28,1),
                                          filter .6s ease; }
        .fa-armed .burst > *:nth-child(1) { transform:translate(-150px, 70px) rotate(-11deg) scale(.82); }
        .fa-armed .burst > *:nth-child(2) { transform:translate(0, 130px) rotate(5deg) scale(.86); }
        .fa-armed .burst > *:nth-child(3) { transform:translate(150px, 70px) rotate(11deg) scale(.82); }
        .fa-armed .burst > *:nth-child(4) { transform:translate(120px, -90px) rotate(-7deg) scale(.85); }
        .fa-armed .burst.visible > * { opacity:1; filter:none; transform:none; }
        .fa-armed .burst.visible > *:nth-child(1) { transition-delay:.04s; }
        .fa-armed .burst.visible > *:nth-child(2) { transition-delay:.14s; }
        .fa-armed .burst.visible > *:nth-child(3) { transition-delay:.24s; }
        .fa-armed .burst.visible > *:nth-child(4) { transition-delay:.34s; }

        .fa-g2 { display:grid; grid-template-columns:1fr 1fr; gap:18px; }
        .fa-g3 { display:grid; grid-template-columns:repeat(3,1fr); gap:18px; }

        @media (max-width:920px) {
          .fa-bg::before { background-attachment:scroll; }  /* 'fixed' saccade sur mobile */
          .fa-armed .reveal.reveal-l { transform:translateX(-40px); }
          .fa-armed .reveal.reveal-r { transform:translateX(40px); }
          .fa-armed .burst > *:nth-child(n) { transform:translateY(60px) scale(.94); }
          .fa-cut { clip-path:none; padding-top:56px; margin-top:0; }
          .fa-hero-in { grid-template-columns:1fr; gap:48px; }
          .fa-phone-holder { transform:scale(.92); }
          .fa-win { transform:none; }
        }
        @media (max-width:860px) {
          .fa-g3, .fa-split { grid-template-columns:1fr; gap:26px; }
          .fa-h2 { font-size:25px; }
          .fa-sec { padding:56px 0; }
        }
        @media (max-width:860px) {
          .fa-marquee-item { font-size:21px; padding:18px 0; margin-right:36px; gap:16px; }
          .fa-marquee-dot { width:8px; height:8px; }
        }
        @media (max-width:640px) {
          .fa-g2 { grid-template-columns:1fr; }
          .fa-badge-float { display:none; }  /* chevauchent l'écran sur petit mobile */
          .fa-phone-holder { transform:scale(.88); margin:-22px 0; }
        }

        @media (prefers-reduced-motion: reduce) {
          .fa-armed .reveal, .fa-armed .reveal.reveal-l, .fa-armed .reveal.reveal-r,
          .fa-armed .burst > * { opacity:1; transform:none; filter:none; transition:none; }
          .fa-bg::before { background-attachment:scroll; }
          .fa-blob, .fa-phone-holder, .fa-badge-float, .fa-wave span, .fa-avatar::after,
          .fa-grad, .fa-cta::after, .fa-skeleton, .fa-live i, .fa-marquee-track { animation:none; }
          .fa-armed .fa-step::before { transform:scaleY(1); }
          .fa-chk, .fa-row { opacity:1; transform:none; animation:none; }
        }
      `}</style>

      {/* ===== BANDEAU DÉFILANT (au-dessus du hero) ===== */}
      <div className="fa-marquee-band">
        <div className="fa-marquee">
          <div className="fa-marquee-track">
            {[...bandeau, ...bandeau].map((phrase, i) => (
              <span className="fa-marquee-item" key={i}>
                <i className="fa-marquee-dot" />{phrase}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ===== HERO ===== */}
      <header className="fa-hero">
        <span className="fa-blob b1" /><span className="fa-blob b2" /><span className="fa-blob b3" />
        <div className="fa-wrap">
          <div className="fa-hero-in">
            <div style={{ color: '#fff' }}>
              <h1 style={{ fontSize: 'clamp(33px,5vw,54px)', margin: '0 0 18px', fontWeight: 800, lineHeight: 1.08, letterSpacing: -1.4 }}>
                <span className="fa-grad">Vous avez du répondant ?</span><br />On a un casque pour vous.
              </h1>
              <p style={{ fontSize: 19, opacity: .93, maxWidth: 520, margin: '0 0 14px', lineHeight: 1.55 }}>
                Le <strong>{NOM_SOCIETE}</strong> recrute ses prochains chargés de dossiers.
                Un métier de téléphone, de conviction et de résultat — on vous apprend tout.
              </p>
              <p style={{ fontSize: 14, opacity: .68, marginBottom: 32 }}>
                Recrutement ouvert · {currentDate}
              </p>
              <button className="fa-cta" onClick={() => goTo('candidature')}>
                Déposer ma candidature <ArrowRight size={18} />
              </button>
            </div>

            <PhoneMockup />
          </div>
        </div>
      </header>

      {/* ===== NAV COLLANTE ===== */}
      <nav className="fa-nav">
        <div className="fa-nav-in">
          {nav.map(([id, label]) => (
            <button key={id} onClick={() => goTo(id)}>{label}</button>
          ))}
        </div>
      </nav>

      {/* ===== LE MÉTIER ===== */}
      <section id="metier" className="fa-sec fa-bg veil-clair" style={{ '--fa-bg': "url('/img/formation/bg-metier.jpg')" }}>
        <div className="fa-wrap">
          <div className="fa-split">
            <div className="reveal reveal-l">
              <div className="fa-kicker">Le métier</div>
              <h2 className="fa-h2">Trois casquettes, un seul téléphone</h2>
              <p className="fa-lead" style={{ marginBottom: 18 }}>
                On appelle des gens qui ont laissé filer des échéances chez un assureur ou une mutuelle,
                et dont le dossier arrive à son terme. On leur dit où ils en sont, on cherche la sortie
                avec eux, et on la trouve. C'est tout le métier — et c'est plus vivant qu'il n'y paraît.
              </p>
              <div style={{
                background: NAVY_SOFT, border: '1px solid #dbe3f5', borderRadius: 12, padding: '16px 18px', fontSize: 14.5
              }}>
                <strong style={{ color: NAVY }}>Ce qu'on est :</strong> un cabinet privé, mandaté par les
                créanciers, à mi-chemin entre le recouvrement, la conciliation et la défense du client.
                Pas un huissier, pas un tribunal — un intermédiaire qui débloque des situations.
              </div>
            </div>
            <img className="fa-photo reveal reveal-r" src={`${IMG}/accord.jpg`} alt="Poignée de main scellant un accord" loading="lazy" />
          </div>

          <div className="fa-g3 burst reveal" style={{ marginTop: 34 }}>
            {troisMetiers.map(m => {
              const Icon = m.icon;
              return (
                <div key={m.t} className="fa-card reveal">
                  <div style={{
                    width: 42, height: 42, borderRadius: 11, background: NAVY_SOFT,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12
                  }}>
                    <Icon size={21} style={{ color: NAVY }} />
                  </div>
                  <div style={{ fontWeight: 800, color: NAVY, marginBottom: 6, fontSize: 17 }}>{m.t}</div>
                  <div style={{ fontSize: 14.5, color: '#5f6a80' }}>{m.d}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== LE RAPPEL : LES CLIENTS NE MENTENT PAS TOUJOURS ===== */}
      <section id="verite" className="fa-sec" style={{ background: NAVY_DARK, color: '#fff' }}>
        <div className="fa-wrap">
          <div className="reveal reveal-l" style={{ maxWidth: 740 }}>
            <div className="fa-kicker on-dark">Le rappel</div>
            <h2 style={{ fontSize: 31, margin: '0 0 16px', fontWeight: 800, letterSpacing: -.7, lineHeight: 1.15 }}>
              Les clients ne mentent pas toujours
            </h2>
            <p style={{ fontSize: 16.5, opacity: .9, margin: '0 0 14px' }}>
              Il y aura des personnes qui vous diront « je n'ai jamais rien signé » et qui le penseront
              vraiment. Et parmi elles, certaines auront raison.
            </p>
            <p style={{ fontSize: 16.5, opacity: .9, margin: 0 }}>
              Ces deux vidéos montrent comment on engage quelqu'un sans qu'il s'en rende compte, et à quoi
              ressemble l'autre bout du fil. Un agent qui a vu ça écoute autrement — et fait bien plus vite
              la différence entre une contestation sincère et une dénégation de confort.
            </p>
          </div>

          <div className="fa-g2 burst reveal" style={{ marginTop: 32 }}>
            {VIDEOS.map(v => (
              <button key={v.id} className="fa-vid" onClick={() => setVideoOuverte(v.id)}>
                <div className="fa-vid-img" style={{ backgroundImage: `url('${IMG}/${v.vignette}.jpg')` }}>
                  <div className="fa-vid-play"><span><Play size={24} fill="currentColor" /></span></div>
                </div>
                <div className="fa-vid-body">
                  <div style={{ fontWeight: 800, fontSize: 16.5, marginBottom: 6 }}>{v.titre}</div>
                  <div style={{ fontSize: 14.5, opacity: .78 }}>{v.resume}</div>
                </div>
              </button>
            ))}
          </div>

          <p style={{ fontSize: 13.5, opacity: .6, margin: '20px 0 0', fontStyle: 'italic' }}>
            → C'est exactement pour ces personnes-là que l'instruction existe. Quand la contestation tient,
            on ne les fait pas payer : on va chercher leur remboursement.
          </p>
        </div>
      </section>

      {/* ===== LE DOSSIER ===== */}
      <section className="fa-sec fa-bg veil-blanc fa-cut" style={{ '--fa-bg': "url('/img/formation/bg-dossier.jpg')" }}>
        <div className="fa-wrap">
          <div className="fa-split">
            <div className="reveal reveal-l"><DossierMockup /></div>
            <div className="reveal reveal-r">
              <div className="fa-kicker">Vos outils</div>
              <h2 className="fa-h2">Chaque dossier est expertisé avant d'arriver sur votre poste</h2>
              <p className="fa-lead" style={{ marginBottom: 18 }}>
                Aucun dossier n'entre chez nous sans <strong>deux pièces obligatoires</strong> : un contrat
                signé électroniquement, et un enregistrement rattaché au numéro sur lequel vous appelez.
                Sans ces deux éléments, le dossier est refusé à l'entrée — il n'arrivera jamais devant vous.
              </p>
              {pieces.map((p, i) => (
                <div key={p.t} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 13 }}>
                  <span style={{
                    flex: '0 0 auto', background: NAVY, color: '#fff', borderRadius: 7,
                    padding: '3px 8px', fontSize: 11.5, fontWeight: 800, marginTop: 3
                  }}>{`0${i + 1}`}</span>
                  <div style={{ fontSize: 14.5 }}>
                    <strong style={{ color: NAVY }}>{p.t}</strong><br />
                    <span style={{ color: '#6b7590' }}>{p.d}</span>
                  </div>
                </div>
              ))}
              <div style={{
                background: NAVY_SOFT, border: '1px solid #dbe3f5', borderRadius: 12,
                padding: '16px 18px', fontSize: 14.5, marginTop: 18
              }}>
                <strong style={{ color: NAVY }}>Ce que ça change pour vous :</strong> le tri est fait en amont
                par le service dossiers. Vous ne voyez pas tout le détail à l'écran, et c'est normal —
                l'essentiel a déjà été contrôlé avant que le dossier vous soit attribué. Vous décrochez donc
                avec un dossier solide derrière vous : pas d'hésitation dans la voix, pas d'excuses,
                vous savez exactement pourquoi vous appelez.
              </div>
              <p className="fa-note">
                → Et si la personne conteste malgré tout, vous n'avez rien à trancher au téléphone :
                vous notez, et le service dossiers reprend la main.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== LA PROCÉDURE ===== */}
      <section id="procedure" className="fa-sec fa-bg veil-fort fa-cut" style={{ '--fa-bg': "url('/img/formation/bg-procedure.jpg')" }}>
        <div className="fa-wrap">
          <div className="reveal" style={{ maxWidth: 740 }}>
            <div className="fa-kicker">S'il ne règle pas</div>
            <h2 className="fa-h2">Ce qui l'attend ensuite, étape par étape</h2>
            <p className="fa-lead">
              C'est l'argument le plus utile de votre boîte à outils — à condition de le connaître par cœur.
              Un client qui comprend précisément ce qui l'attend règle beaucoup plus souvent qu'un client
              à qui on agite une menace floue. Voici la chaîne complète, telle qu'elle se déroule vraiment.
            </p>
          </div>

          <div className="reveal" style={{ marginTop: 30 }}>
            {procedure.map((e, k) => {
              const Icon = e.icon;
              return (
                <div key={e.t} className="fa-step reveal">
                  <div className="fa-step-n">
                    <Icon size={21} style={{ color: NAVY }} />
                  </div>
                  <div style={{ paddingTop: 3 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 5 }}>
                      <span style={{
                        background: NAVY_SOFT, color: NAVY, borderRadius: 6, padding: '2px 8px',
                        fontSize: 11.5, fontWeight: 800
                      }}>{`ÉTAPE 0${k + 1}`}</span>
                      <strong style={{ color: NAVY, fontSize: 17 }}>{e.t}</strong>
                    </div>
                    <p style={{ margin: 0, fontSize: 14.5, color: '#5f6a80' }}>{e.d}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="fa-g2 burst reveal" style={{ marginTop: 30 }}>
            <div style={{
              background: '#fff', border: '1px solid #e3e8f3', borderRadius: 13, padding: '18px 20px'
            }}>
              <div style={{ fontWeight: 800, color: NAVY, marginBottom: 7, fontSize: 15.5 }}>
                La question qu'on vous posera le plus
              </div>
              <div style={{ fontSize: 14.5, color: '#5f6a80' }}>
                « Est-ce que je vais être fiché à la Banque de France ? » — Non. Le fichage Banque de France
                concerne les incidents de crédit, pas ce type d'impayé. Répondez-le franchement : c'est le
                genre de précision qui fait basculer un client méfiant de votre côté.
              </div>
            </div>
            <div style={{
              background: '#fff', border: '1px solid #e3e8f3', borderRadius: 13, padding: '18px 20px'
            }}>
              <div style={{ fontWeight: 800, color: NAVY, marginBottom: 7, fontSize: 15.5 }}>
                Pourquoi la date d'échéance compte vraiment
              </div>
              <div style={{ fontSize: 14.5, color: '#5f6a80' }}>
                En assurance, le créancier dispose d'une fenêtre limitée pour agir. Cette date-là est réelle
                et figure au dossier : vous n'avez aucun besoin d'en inventer une, celle-ci suffit largement
                à créer la décision.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== LES 3 ISSUES ===== */}
      <section id="issues" className="fa-sec fa-bg veil-blanc fa-cut" style={{ '--fa-bg': "url('/img/formation/bg-job.jpg')" }}>
        <div className="fa-wrap">
          <div className="reveal">
            <div className="fa-kicker">Le cœur du job</div>
            <h2 className="fa-h2">Un appel, trois issues possibles</h2>
            <p className="fa-lead">
              Votre boulot : reconnaître en quelques phrases laquelle des trois s'ouvre, et l'emmener au bout.
              C'est là que se fait la différence entre un agent moyen et un très bon.
            </p>
          </div>
          <div className="fa-g3 burst reveal">
            {issues.map(iss => {
              const Icon = iss.icon;
              return (
                <div key={iss.n} className="fa-issue reveal">
                  <div className="fa-issue-wrap" style={{ position: 'relative' }}>
                    <div className="fa-issue-img" style={{ backgroundImage: `url('${IMG}/${iss.img}.jpg')` }} />
                    <span className="fa-issue-n" style={{ background: iss.color }}>{iss.n}</span>
                  </div>
                  <div style={{ padding: '18px 20px 22px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <Icon size={18} style={{ color: iss.color, flex: '0 0 auto' }} />
                      <strong style={{ color: iss.color, fontSize: 16.5 }}>{iss.titre}</strong>
                    </div>
                    <p style={{ margin: '0 0 10px', fontSize: 14.5 }}>{iss.court}</p>
                    <p style={{ margin: 0, fontSize: 13.5, color: '#6b7590' }}>{iss.long}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== FORMATION ===== */}
      <section id="formation" className="fa-sec">
        <div className="fa-wrap">
          <div className="reveal" style={{
            borderRadius: 18, overflow: 'hidden', marginBottom: 28, position: 'relative',
            minHeight: 210, display: 'flex', alignItems: 'flex-end',
            background: `linear-gradient(90deg, ${NAVY_DARK}ee 0%, ${NAVY}aa 60%, transparent 100%), url('${IMG}/formation.jpg') center/cover`
          }}>
            <div style={{ padding: '26px 28px', color: '#fff' }}>
              <div style={{ fontSize: 12, letterSpacing: 3, textTransform: 'uppercase', opacity: .8, fontWeight: 700 }}>
                Le programme
              </div>
              <h2 style={{ fontSize: 27, margin: '6px 0 6px', fontWeight: 800 }}>Quatre modules, et vous décrochez</h2>
              <p style={{ margin: 0, opacity: .9, fontSize: 15 }}>
                Mise en situation avec un formateur, puis double écoute sur le plateau. Vous n'êtes jamais
                lâché seul sur votre premier appel.
              </p>
            </div>
          </div>

          <div className="reveal">
            {modules.map((m, i) => {
              const Icon = m.icon;
              const open = openModule === i;
              return (
                <div key={m.titre} className="fa-mod">
                  <button className="fa-mod-head" onClick={() => setOpenModule(open ? -1 : i)}>
                    <span style={{
                      width: 34, height: 34, borderRadius: 9, background: NAVY_SOFT, flex: '0 0 auto',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <Icon size={17} style={{ color: NAVY }} />
                    </span>
                    <span style={{ flex: 1 }}>
                      <span style={{ color: '#93a0c0', marginRight: 8 }}>{`0${i + 1}`}</span>{m.titre}
                    </span>
                    <ChevronDown size={19} style={{
                      color: '#93a0c0', flex: '0 0 auto',
                      transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .25s'
                    }} />
                  </button>
                  {open && (
                    <div className="fa-mod-body">
                      <p style={{ margin: 0, fontSize: 14.5 }}>{m.resume}</p>
                      <div className="fa-script">{m.extrait}</div>
                      <p className="fa-note">→ {m.note}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== CE QU'ON ATTEND ===== */}
      <section id="cadre" className="fa-sec" style={{ background: NAVY, color: '#fff' }}>
        <div className="fa-wrap">
          <div className="reveal" style={{ maxWidth: 720 }}>
            <div className="fa-kicker on-dark">Franchement</div>
            <h2 style={{ fontSize: 30, margin: '0 0 16px', fontWeight: 800, letterSpacing: -.5 }}>
              Ce n'est pas un poste tranquille
            </h2>
            <p style={{ fontSize: 16.5, opacity: .92, margin: '0 0 14px' }}>
              On vous raccrochera au nez. On vous dira non avant même que vous ayez fini votre phrase.
              Et le lendemain, vous décrocherez un dossier que personne n'avait réussi à débloquer.
            </p>
            <p style={{ fontSize: 16.5, opacity: .92, margin: 0 }}>
              Ici on compte les dossiers réglés, pas les appels passés. Si vous aimez qu'on vous mette
              au défi et que vous n'abandonnez pas au premier refus, vous allez vous plaire.
            </p>
          </div>

          <div className="fa-g3 burst reveal" style={{ marginTop: 30 }}>
            {[
              ['Un non ne clôt rien', "C'est souvent le vrai début de la conversation. On change d'angle, on repose une question, on avance."],
              ['On joue collectif', "Double écoute, débriefs, coups de main sur les dossiers durs. Personne ne rame seul sur le plateau."],
              ['On progresse vite', "Formation complète au départ, puis montée en responsabilité selon ce que vous rendez au téléphone."]
            ].map(([t, d]) => (
              <div key={t} style={{
                background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.18)',
                borderRadius: 13, padding: '18px 20px'
              }}>
                <div style={{ fontWeight: 800, marginBottom: 6, fontSize: 16 }}>{t}</div>
                <div style={{ fontSize: 14.5, opacity: .85 }}>{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PROFIL ===== */}
      <section className="fa-sec fa-bg veil-clair fa-cut" style={{ '--fa-bg': "url('/img/formation/bg-profil.jpg')" }}>
        <div className="fa-wrap">
          <div className="fa-split">
            <div className="reveal reveal-l">
              <div className="fa-kicker">Vous</div>
              <h2 className="fa-h2">On ne regarde pas votre CV</h2>
              <p className="fa-lead" style={{ marginBottom: 20 }}>
                Aucun diplôme exigé, aucune expérience obligatoire. Ce qu'on veut savoir, c'est comment
                vous réagissez quand on vous dit non.
              </p>
              <div className="fa-g2 burst reveal">
                {[
                  { icon: Headphones, t: 'Du répondant', d: "Une voix claire, de la répartie, et l'envie de convaincre." },
                  { icon: Scale, t: 'Du sang-froid', d: "On tombe sur des gens remontés. Vous encaissez et vous repartez." },
                  { icon: ShieldCheck, t: 'De la ténacité', d: "Vous ne lâchez pas un dossier parce qu'on vous a dit non une fois." },
                  { icon: BadgeCheck, t: 'De la régularité', d: "Horaires fixes, 8h-21h, ni dimanche ni jours fériés. On tient le rythme." }
                ].map(p => {
                  const Icon = p.icon;
                  return (
                    <div key={p.t} style={{ border: '1px solid #e3e8f3', borderRadius: 12, padding: '15px 16px' }}>
                      <Icon size={19} style={{ color: NAVY, marginBottom: 7 }} />
                      <div style={{ fontWeight: 700, color: NAVY, marginBottom: 4, fontSize: 15 }}>{p.t}</div>
                      <div style={{ fontSize: 13.5, color: '#6b7590' }}>{p.d}</div>
                    </div>
                  );
                })}
              </div>
            </div>
            <img className="fa-photo reveal reveal-r" src={`${IMG}/equipe.jpg`} alt="Équipe au travail dans un bureau" loading="lazy" />
          </div>
        </div>
      </section>

      {/* ===== CANDIDATURE ===== */}
      <section id="candidature" style={{ background: NAVY, color: '#fff', padding: '58px 0' }}>
        <div className="fa-wrap">
          {success ? (
            <div className="reveal" style={{
              background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.25)',
              borderRadius: 16, padding: '40px 28px', textAlign: 'center', maxWidth: 620, margin: '0 auto'
            }}>
              <CheckCircle2 size={46} style={{ marginBottom: 14 }} />
              <h2 style={{ margin: '0 0 10px', fontSize: 24, fontWeight: 800 }}>Candidature enregistrée</h2>
              <p style={{ margin: 0, opacity: .9 }}>
                Merci. Votre dossier est transmis à l'équipe de recrutement.
                <br />Nous vous contactons très vite sur votre WhatsApp.
              </p>
            </div>
          ) : (
            <div className="fa-split reveal" style={{ alignItems: 'center' }}>
              <div>
                <div className="fa-kicker on-dark">Rejoignez-nous</div>
                <h2 style={{ fontSize: 30, margin: '0 0 14px', fontWeight: 800, letterSpacing: -.5 }}>
                  On se parle ?
                </h2>
                <p style={{ opacity: .88, fontSize: 16, margin: '0 0 10px' }}>
                  Trois informations, trente secondes. On vous écrit sur WhatsApp pour un premier échange,
                  puis on fixe un entretien.
                </p>
                <p style={{ opacity: .72, fontSize: 14.5, margin: 0 }}>
                  Rémunération et primes détaillées dès le premier appel.
                </p>
              </div>

              <div style={{ background: '#fff', borderRadius: 16, padding: '26px 24px', boxShadow: '0 20px 50px rgba(0,0,0,.25)' }}>
                <form onSubmit={handleSubmit}>
                  <div style={{ marginBottom: 13 }}>
                    <label style={lbl}>Nom et prénom <span style={{ color: '#c62828' }}>*</span></label>
                    <input className="fa-input" type="text" required placeholder="Jean Dupont"
                      value={formData.nom}
                      onChange={(e) => setFormData({ ...formData, nom: e.target.value })} />
                  </div>
                  <div style={{ marginBottom: 13 }}>
                    <label style={lbl}>Numéro WhatsApp <span style={{ color: '#c62828' }}>*</span></label>
                    <input className="fa-input" type="tel" required placeholder="+33 6 12 34 56 78"
                      value={formData.telephone_whatsapp}
                      onChange={(e) => setFormData({ ...formData, telephone_whatsapp: e.target.value })} />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={lbl}>Email professionnel <span style={{ color: '#c62828' }}>*</span></label>
                    <input className="fa-input" type="email" required placeholder="prenom.nom@exemple.fr"
                      value={formData.email_pro}
                      onChange={(e) => setFormData({ ...formData, email_pro: e.target.value })} />
                  </div>

                  {error && (
                    <div style={{
                      background: '#fdf3f3', border: '1px solid #f0d5d5', color: '#8a2020',
                      borderRadius: 9, padding: '11px 14px', marginBottom: 14, fontSize: 14
                    }}>{error}</div>
                  )}

                  <button type="submit" className="fa-cta" disabled={loading} style={{ width: '100%', justifyContent: 'center' }}>
                    <Send size={17} />{loading ? 'Envoi en cours…' : 'Envoyer ma candidature'}
                  </button>
                  <p style={{ fontSize: 12.5, color: '#57627d', margin: '13px 0 0', textAlign: 'center' }}>
                    Vos coordonnées servent uniquement au traitement de votre candidature.
                  </p>
                </form>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ===== PIED DE PAGE ===== */}
      <footer style={{ background: NAVY_DARK, color: 'rgba(255,255,255,.8)', padding: '24px 0' }}>
        <div className="fa-wrap" style={{ fontSize: 13, textAlign: 'center', lineHeight: 1.9 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 4 }}>
            <Handshake size={15} /> <Scale size={15} />
            <strong style={{ color: '#fff' }}>{NOM_SOCIETE}</strong>
          </div>
          Recouvrement amiable · Conciliation · Défense du client · 01 89 26 67 18
          <br />Page de recrutement · {currentDate}
        </div>
      </footer>
    </div>
  );
}

const lbl = { display: 'block', fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 6 };

export default FormationAssurance;
